<template>
  <div class="chart-control-panel">
    <!-- Top row: Dataset + View + Live toggle -->
    <div class="chart-control-panel__row">
      <div class="chart-control-panel__item">
        <label class="chart-control-panel__label">Modelo</label>
        <v-select :value="dataset" @input="$emit('update:dataset', $event)" :options="datasetOptions"
          :reduce="opt => opt.value" :clearable="false" label="label" class="chart-control-panel__select" append-to-body
          :calculate-position="withPopper" />
      </div>

      <div v-if="fieldOptions.length > 0" class="chart-control-panel__item">
        <label class="chart-control-panel__label">Campo</label>
        <v-select :value="field" @input="$emit('update:field', $event)" :options="fieldOptions"
          :reduce="opt => opt.value" :clearable="true" :disabled="!dataset" placeholder="Sin agrupar" label="label"
          class="chart-control-panel__select" append-to-body :calculate-position="withPopper" />
      </div>

      <div v-if="showField2 && field2Options.length > 0" class="chart-control-panel__item">
        <label class="chart-control-panel__label">Campo 2</label>
        <v-select :value="field2" @input="$emit('update:field2', $event)" :options="field2Options"
          :reduce="opt => opt.value" :clearable="true" :disabled="!dataset" placeholder="Ninguno" label="label"
          class="chart-control-panel__select" append-to-body :calculate-position="withPopper" />
      </div>

      <div v-if="metricOptions.length > 0" class="chart-control-panel__item">
        <label class="chart-control-panel__label">Métrica</label>
        <v-select :value="metric" @input="$emit('update:metric', $event)" :options="metricOptions"
          :reduce="opt => opt.value" :clearable="false" :disabled="!dataset" label="label"
          class="chart-control-panel__select" append-to-body :calculate-position="withPopper" />
      </div>

      <div class="chart-control-panel__item">
        <label class="chart-control-panel__label">Gráfica</label>
        <v-select :value="view" @input="$emit('update:view', $event)" :options="viewOptions" :reduce="opt => opt.value"
          :clearable="false" :disabled="!dataset" label="label" class="chart-control-panel__select" append-to-body
          :calculate-position="withPopper" />
      </div>

      <div v-if="view === 'bar'" class="chart-control-panel__item chart-control-panel__item--toggle mx-50">
        <label class="chart-control-panel__label">Agrupado</label>
        <div class="chart-control-panel__control-row">
          <button type="button" class="chart-control-panel__toggle" :class="{ 'chart-control-panel__toggle--on': stackGroup }"
            @click="$emit('update:stackGroup', !stackGroup)" :disabled="!dataset || !field2">
            <span class="chart-control-panel__toggle-knob" />
          </button>
        </div>
      </div>

      <div v-if="topRankedOffered"
        class="chart-control-panel__item chart-control-panel__item--toggle chart-control-panel__item--top-n mx-50"
        :title="topRankedAvailable
          ? 'Muestra solo los N elementos con más registros'
          : 'Requiere una gráfica de categorías y un campo con filtro propio'">
        <label class="chart-control-panel__label">Top por registros</label>
        <div class="chart-control-panel__top-n-control">
          <button type="button" class="chart-control-panel__toggle"
            :class="{ 'chart-control-panel__toggle--on': topRankedEnabled }"
            :disabled="!dataset || !topRankedAvailable" role="switch"
            :aria-checked="String(topRankedEnabled)"
            aria-label="Activar top por número de registros"
            @click="$emit('update:topRankedEnabled', !topRankedEnabled)">
            <span class="chart-control-panel__toggle-knob" />
          </button>
          <input v-if="topRankedEnabled && topRankedAvailable" type="number"
            class="chart-control-panel__top-n-input"
            :value="topRankedCount" :min="topRankedMin" :max="topRankedMax" step="1" inputmode="numeric"
            :disabled="!dataset" aria-label="Cantidad de elementos del top"
            @input="$emit('update:topRankedCount', $event.target.value)" @blur="$emit('commit-top-ranked')"
            @keydown.enter="$event.target.blur()" />
          <span v-if="topRankedEnabled && topRankedAvailable && topRankedLoading"
            class="chart-control-panel__top-n-spinner" aria-hidden="true" />
        </div>
        <small v-if="view && !topRankedAvailable" class="chart-control-panel__top-n-note">
          {{ topRankedUnavailableReason }}
        </small>
        <small v-else-if="topRankedEnabled && topRankedError" class="chart-control-panel__top-n-note">
          {{ topRankedError }}
        </small>
      </div>

      <div class="chart-control-panel__item chart-control-panel__item--toggle mx-50">
        <label class="chart-control-panel__label">En vivo</label>
        <div class="chart-control-panel__control-row">
          <button type="button" class="chart-control-panel__toggle" :class="{ 'chart-control-panel__toggle--on': live }"
            @click="$emit('update:live', !live)" :disabled="!dataset">
            <span class="chart-control-panel__toggle-knob" />
          </button>
        </div>
      </div>

      <div class="chart-control-panel__item chart-control-panel__item--toggle">
        <label class="chart-control-panel__label">Descargar</label>
        <div class="chart-control-panel__download-group">
          <button type="button" class="chart-control-panel__download"
            :disabled="!dataset || topRankedActive" @click="$emit('download-csv')"
            :title="topRankedActive
              ? 'No disponible con el top por registros activo: el Excel no queda recortado al top'
              : 'Descargar XLSX'">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"
              stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
          </button>
          <button type="button" class="chart-control-panel__download chart-control-panel__download--pdf" :disabled="!dataset" @click="$emit('download-pdf')"
            title="Descargar PDF">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"
              stroke-linecap="round" stroke-linejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
              <polyline points="10 9 9 9 8 9" />
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Time filter row (below) -->
    <div v-if="showTimeRange" class="chart-control-panel__time-row">
      <ChartTimeFilter :from="from" :to="to" :t="t" @update:from="$emit('update:from', $event)"
        @update:to="$emit('update:to', $event)" />
    </div>

    <!-- Date grouping (freq) row -->
    <div v-if="dateGroupingPresets.length > 0" class="chart-control-panel__freq-row">
      <label class="chart-control-panel__label">Agrupar por periodo</label>
      <div class="chart-control-panel__chips">
        <button v-for="opt in dateGroupingPresets" :key="opt.value" type="button" class="chart-control-panel__chip"
          :class="{ 'chart-control-panel__chip--active': freq === opt.value }"
          @click="$emit('update:freq', freq === opt.value ? '' : opt.value)">
          {{ opt.label }}
        </button>
      </div>
    </div>

  </div>
</template>

<script>
import { computed } from '@vue/composition-api'
import vSelect from 'vue-select'
import ChartTimeFilter from './ChartTimeFilter.vue'
import popperHelper from '@/libs/popper-helper'

export default {
  name: 'ChartControlPanel',

  components: { vSelect, ChartTimeFilter },

  props: {
    dataset: { type: String, default: '' },
    view: { type: String, default: '' },
    field: { type: String, default: '' },
    field2: { type: String, default: '' },
    from: { type: String, default: '' },
    to: { type: String, default: '' },
    showTimeRange: { type: Boolean, default: true },
    live: { type: Boolean, default: true },
    stackGroup: { type: Boolean, default: false },
    topRankedEnabled: { type: Boolean, default: false },
    topRankedCount: { type: [String, Number], default: 5 },
    topRankedAvailable: { type: Boolean, default: false },
    topRankedOffered: { type: Boolean, default: false },
    topRankedActive: { type: Boolean, default: false },
    topRankedLoading: { type: Boolean, default: false },
    topRankedError: { type: String, default: '' },
    topRankedUnavailableReason: { type: String, default: '' },
    topRankedMin: { type: Number, default: 1 },
    topRankedMax: { type: Number, default: 100 },
    datasetOptions: { type: Array, default: () => [] },
    viewOptions: { type: Array, default: () => [] },
    fieldOptions: { type: Array, default: () => [] },
    freq: { type: String, default: '' },
    dateGroupingPresets: { type: Array, default: () => [] },
    isProcessFormField: { type: Boolean, default: false },
    showField2: { type: Boolean, default: false },
    field2FieldOptions: { type: Array, default: () => [] },
    metric: { type: String, default: '' },
    metricOptions: { type: Array, default: () => [] },
    t: { type: Function, required: true },
  },

  setup(props) {
    const field2Options = computed(() => {
      // When dedicated field2 options are provided (timeseries), use those
      if (props.field2FieldOptions.length > 0) return props.field2FieldOptions
      // Otherwise fall back to all fields except the selected one
      return props.fieldOptions.filter(opt => opt.value !== props.field)
    })

    return {
      field2Options,
      withPopper: popperHelper.withPopper,
    }
  },
}
</script>

<style scoped>
.chart-control-panel {
  display: flex;
  flex-direction: column;
  gap: 10px;
  --ccp-control-height: 37px;
}

.chart-control-panel__control-row,
.chart-control-panel__top-n-control,
.chart-control-panel__download-group {
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: var(--ccp-control-height);
}

.chart-control-panel__row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-start;
}

.chart-control-panel__item {
  flex: 1 1 275px;
  min-width: 220px;
  max-width: 360px;
}

.chart-control-panel__label {
  display: block;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 4px;
  opacity: 0.7;
}

.chart-control-panel__select {
  font-size: 0.85rem;
}

/* -- Toggle -- */
.chart-control-panel__item--toggle {
  flex: 0 0 auto;
  min-width: auto;
  max-width: none;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.chart-control-panel__toggle {
  position: relative;
  width: 40px;
  height: 22px;
  border-radius: 11px;
  border: none;
  cursor: pointer;
  background: #555;
  transition: background 0.2s;
  padding: 0;
}

.chart-control-panel__toggle:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.chart-control-panel__toggle--on {
  background: #28c76f;
}

.chart-control-panel__toggle-knob {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #fff;
  transition: transform 0.2s;
}

.chart-control-panel__toggle--on .chart-control-panel__toggle-knob {
  transform: translateX(18px);
}

/* -- Top N by record count -- */
.chart-control-panel__item--top-n {
  min-width: 130px;
}

.chart-control-panel__top-n-input {
  width: 64px;
  height: 28px;
  padding: 2px 7px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 5px;
  background: rgba(255, 255, 255, 0.06);
  color: inherit;
  font-size: 0.8rem;
  outline: none;
  transition: border-color 0.2s;
}

.chart-control-panel__top-n-input:hover:not(:disabled) {
  border-color: rgba(255, 255, 255, 0.2);
}

.chart-control-panel__top-n-input:focus {
  border-color: #0068c9;
}

.chart-control-panel__top-n-input:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.chart-control-panel__top-n-spinner {
  width: 12px;
  height: 12px;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  opacity: 0.6;
  animation: chart-control-panel-spin 0.7s linear infinite;
}

@keyframes chart-control-panel-spin {
  to { transform: rotate(360deg); }
}

.chart-control-panel__top-n-note {
  max-width: 130px;
  margin-top: 3px;
  font-size: 0.62rem;
  line-height: 1.15;
  opacity: 0.55;
}

/* -- Download buttons -- */
.chart-control-panel__download {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 34px;
  padding: 0 12px;
  border: 1px solid rgba(0, 104, 201, 0.35);
  border-radius: 6px;
  background: rgba(0, 104, 201, 0.12);
  color: #5ba3e6;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s, transform 0.1s;
}

.chart-control-panel__download:hover:not(:disabled) {
  background: rgba(0, 104, 201, 0.25);
  border-color: rgba(0, 104, 201, 0.5);
}

.chart-control-panel__download:active:not(:disabled) {
  transform: scale(0.95);
}

.chart-control-panel__download:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

/* .chart-control-panel__download--pdf {
  border-color: rgba(201, 50, 0, 0.35);
  background: rgba(201, 50, 0, 0.12);
  color: #e6735b;
}

.chart-control-panel__download--pdf:hover:not(:disabled) {
  background: rgba(201, 50, 0, 0.25);
  border-color: rgba(201, 50, 0, 0.5);
} */

/* Time row */
.chart-control-panel__time-row {
  padding-top: 6px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

/* Optional label */
.chart-control-panel__optional {
  font-weight: 400;
  text-transform: none;
  opacity: 0.5;
  font-size: 0.65rem;
}

/* Freq row */
.chart-control-panel__freq-row {
  padding-top: 6px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.chart-control-panel__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
}

.chart-control-panel__chip {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  font-size: 0.75rem;
  font-weight: 500;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.06);
  color: inherit;
  cursor: pointer;
  transition: all 0.15s;
}

.chart-control-panel__chip:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.2);
}

.chart-control-panel__chip--active {
  background: rgba(0, 104, 201, 0.25);
  border-color: rgba(0, 104, 201, 0.5);
  color: #5ba3e6;
}
</style>
